/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  WidgetDashboard,
  WidgetDashboard$inboundSchema,
} from "./widgetdashboard.js";

/**
 * A named question against a catalog dataset together with how it is drawn. Belongs to a project and is visible to everyone in the organization. Widgets are the building block dashboards will place.
 */
export type Widget = {
  createdAt: Date;
  /**
   * Who saved it, when known
   */
  createdByUserId?: string | undefined;
  /**
   * The dashboards this widget is placed on, each once, by name. Widgets are linked, not copied: an edit reaches all of them, and a delete removes it from all of them.
   */
  dashboards: Array<WidgetDashboard>;
  /**
   * The catalog dataset the widget asks
   */
  dataset: string;
  /**
   * What the widget is for, when its creator said
   */
  description?: string | undefined;
  id: string;
  /**
   * Present when the widget no longer works, naming what is wrong: a field the catalog dropped, or a chart that cannot draw the question. A catalog change fails visibly rather than returning wrong numbers.
   */
  invalidReason?: string | undefined;
  /**
   * Display name. Not unique: two people saving the same name is normal.
   */
  name: string;
  organizationId: string;
  projectId: string;
  /**
   * The question: window, grain, dimensions, measures, filters, order and limit. Planned against the catalog on save and on read.
   */
  query: { [k: string]: any };
  updatedAt: Date;
  /**
   * How the question is drawn: a chart type and its options. The client owns the chart vocabulary; the server checks only that the chart can draw the question.
   */
  visualization: { [k: string]: any };
};

/** @internal */
export const Widget$inboundSchema: z.ZodMiniType<Widget, unknown> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    created_by_user_id: z.optional(z.string()),
    dashboards: z.array(WidgetDashboard$inboundSchema),
    dataset: z.string(),
    description: z.optional(z.string()),
    id: z.string(),
    invalid_reason: z.optional(z.string()),
    name: z.string(),
    organization_id: z.string(),
    project_id: z.string(),
    query: z.record(z.string(), z.any()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    visualization: z.record(z.string(), z.any()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "created_by_user_id": "createdByUserId",
      "invalid_reason": "invalidReason",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "updated_at": "updatedAt",
    });
  }),
);

export function widgetFromJSON(
  jsonString: string,
): SafeParseResult<Widget, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Widget$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Widget' from JSON`,
  );
}
